import { useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Archive, ArchiveRestore, Copy, Download, ExternalLink, Pencil, Trash2, UserMinus, UserPlus } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { api, errorMessage } from "@/lib/api";
import { copyEmails, downloadCsv } from "@/lib/exports";
import { useAuth } from "@/context/AuthContext";
import { BackLink } from "@/components/common/Detail";
import { PersonAvatar } from "@/components/common/Visuals";
import { ConfirmDialog } from "@/components/common/Dialogs";
import { EmptyState, ErrorState, LoadingGrid, LoginPrompt } from "@/components/common/States";
import { formatDate, plural } from "@/lib/format";

export default function ListDetailPage() {
  const { id } = useParams();
  const { isAuthed, isAdmin, loading } = useAuth();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [renaming, setRenaming] = useState(false);
  const [name, setName] = useState("");
  const [desc, setDesc] = useState("");
  const [confirm, setConfirm] = useState(false);
  const [busy, setBusy] = useState(false);
  const list = useQuery({ queryKey: ["list", id], queryFn: async () => (await api.get(`/lists/${id}`)).data, enabled: isAuthed });

  if (loading) return null;
  if (!isAuthed) return <div className="lm-container py-12"><LoginPrompt title="Les listes sont réservées aux membres de La Mêlée" /></div>;
  if (list.isLoading) return <div className="lm-container py-10"><LoadingGrid count={3} /></div>;
  if (list.isError) {
    return <div className="lm-container py-10">{list.error.response?.status === 404
      ? <EmptyState title="Liste introuvable" action={<Button asChild className="rounded-full"><Link to="/listes">Retour aux listes</Link></Button>} />
      : <ErrorState onRetry={list.refetch} />}</div>;
  }
  const l = list.data;
  const refresh = () => { queryClient.invalidateQueries({ queryKey: ["list", id] }); queryClient.invalidateQueries({ queryKey: ["lists"] }); };
  const patch = async (payload, message) => {
    setBusy(true);
    try {
      await api.patch(`/lists/${id}`, payload);
      toast.success(message);
      refresh();
      setRenaming(false);
    } catch (error) {
      toast.error(errorMessage(error));
    } finally {
      setBusy(false);
    }
  };
  const removeMember = async (person) => {
    try {
      await api.post(`/lists/${id}/members`, { person_ids: [person.id], action: "remove" });
      toast.success(`${person.full_name} retiré(e) de la liste`);
      refresh();
    } catch (error) {
      toast.error(errorMessage(error));
    }
  };
  const remove = async () => {
    setBusy(true);
    try {
      await api.delete(`/lists/${id}`);
      toast.success("Liste supprimée");
      queryClient.invalidateQueries({ queryKey: ["lists"] });
      navigate("/listes");
    } catch (error) {
      toast.error(errorMessage(error));
      setBusy(false);
    }
  };
  const editable = l.can_edit && !l.archived;

  return (
    <div className="lm-container pb-20">
      <BackLink to="/listes" label="Listes" />
      <section className="lm-card p-6 sm:p-8 lm-fade-up" data-testid="list-detail-header">
        <div className="flex flex-col gap-6 lg:flex-row lg:items-start lg:justify-between">
          <div>
            <div className="flex flex-wrap gap-2 text-[11px] font-bold">
              <span className={l.shared ? "rounded-full bg-sky-50 px-2 py-0.5 text-sky-700" : "rounded-full bg-stone-100 px-2 py-0.5 text-stone-600"}>{l.shared ? "Partagée" : "Privée"}</span>
              {l.archived && <span className="rounded-full bg-amber-50 px-2 py-0.5 text-amber-700" data-testid="list-archived-badge">Archivée</span>}
            </div>
            <h1 className="mt-3 text-3xl font-extrabold sm:text-4xl" data-testid="list-detail-name">{l.name}</h1>
            {l.description && <p className="mt-2 max-w-2xl text-stone-700" data-testid="list-detail-description">{l.description}</p>}
            <p className="mt-2 text-sm text-muted-foreground">
              <b className="text-[#EC7F13]" data-testid="list-members-count">{plural(l.members_count, "contact", "contacts")}</b> · créée par {l.is_mine ? "vous" : l.creator_name || "—"} le {formatDate(l.created_time)} · modifiée le {formatDate(l.last_edited_time)}
            </p>
          </div>
          <div className="flex flex-wrap gap-2">
            <Button className="rounded-full" onClick={() => copyEmails(() => api.get(`/lists/${id}/emails`))} data-testid="list-copy-emails-button"><Copy className="h-4 w-4" /> Copier les emails</Button>
            <Button variant="outline" className="rounded-full" onClick={() => downloadCsv(() => api.get(`/lists/${id}/export.csv`, { responseType: "blob" }))} data-testid="list-export-csv-button"><Download className="h-4 w-4" /> Export CSV</Button>
            {l.notion_url && <Button asChild variant="outline" className="rounded-full" data-testid="list-open-notion-button"><a href={l.notion_url} target="_blank" rel="noreferrer"><ExternalLink className="h-4 w-4" /> Notion</a></Button>}
          </div>
        </div>
        {l.can_manage && (
          <div className="mt-6 flex flex-wrap items-center gap-3 border-t border-border/70 pt-5">
            <Button variant="ghost" size="sm" className="rounded-full" onClick={() => { setName(l.name); setDesc(l.description || ""); setRenaming(true); }} data-testid="list-rename-button"><Pencil className="h-4 w-4" /> Modifier nom / description</Button>
            <Button variant="ghost" size="sm" className="rounded-full" disabled={busy} onClick={() => patch({ archived: !l.archived }, l.archived ? "Liste réactivée" : "Liste archivée")} data-testid="list-archive-button">
              {l.archived ? <><ArchiveRestore className="h-4 w-4" /> Réactiver</> : <><Archive className="h-4 w-4" /> Archiver</>}
            </Button>
            <label className="flex items-center gap-2 text-sm font-semibold">
              <Switch checked={l.shared} disabled={busy} onCheckedChange={(v) => patch({ shared: v }, v ? "Liste partagée avec l'équipe" : "Liste rendue privée")} data-testid="list-shared-switch" />
              Partagée avec l'équipe
            </label>
            {isAdmin && <Button variant="ghost" size="sm" className="ml-auto rounded-full text-red-600 hover:bg-red-50 hover:text-red-700" onClick={() => setConfirm(true)} data-testid="list-delete-button"><Trash2 className="h-4 w-4" /> Supprimer</Button>}
          </div>
        )}
      </section>

      <div className="mb-4 mt-8 flex flex-wrap items-center justify-between gap-3">
        <h2 className="text-xl font-extrabold">Contacts</h2>
        {editable && <Button asChild variant="outline" size="sm" className="rounded-full" data-testid="list-add-contacts-link"><Link to="/personnes"><UserPlus className="h-4 w-4" /> Ajouter depuis le répertoire</Link></Button>}
      </div>
      {!l.members.length ? (
        <EmptyState title="Cette liste est vide" description="Sélectionnez des contacts dans le répertoire Personnes puis choisissez « Ajouter à une liste »." />
      ) : (
        <div className="lm-card overflow-hidden">
          <Table data-testid="list-members-table">
            <TableHeader>
              <TableRow className="bg-stone-50/80 hover:bg-stone-50/80">
                <TableHead>Nom</TableHead><TableHead>Fonction</TableHead><TableHead>Entreprise</TableHead><TableHead>Email</TableHead>
                {editable && <TableHead className="w-12" />}
              </TableRow>
            </TableHeader>
            <TableBody>
              {l.members.map((p) => (
                <TableRow key={p.id} data-testid={`list-member-${p.id}`}>
                  <TableCell>
                    <Link to={`/personnes/${p.id}`} className="flex items-center gap-3 font-semibold hover:text-primary">
                      <PersonAvatar person={p} size="sm" className="rounded-xl" /> {p.full_name}
                    </Link>
                  </TableCell>
                  <TableCell className="max-w-[220px] text-stone-600"><span className="line-clamp-2">{p.job_title}</span></TableCell>
                  <TableCell>{p.organizations.map((o) => o.name).join(", ")}</TableCell>
                  <TableCell className="text-stone-600">{p.email || <span className="text-xs italic text-stone-400">sans email</span>}</TableCell>
                  {editable && (
                    <TableCell>
                      <Button variant="ghost" size="icon" className="text-stone-400 hover:text-red-600" onClick={() => removeMember(p)} aria-label="Retirer" data-testid={`list-remove-member-${p.id}`}>
                        <UserMinus className="h-4 w-4" />
                      </Button>
                    </TableCell>
                  )}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}
      <Dialog open={renaming} onOpenChange={setRenaming}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader><DialogTitle>Modifier la liste</DialogTitle></DialogHeader>
          <Input value={name} onChange={(e) => setName(e.target.value)} data-testid="list-rename-input" />
          <Textarea rows={3} value={desc} onChange={(e) => setDesc(e.target.value)} placeholder="Description" data-testid="list-description-input" />
          <DialogFooter>
            <Button variant="ghost" onClick={() => setRenaming(false)}>Annuler</Button>
            <Button className="rounded-full" disabled={busy} onClick={() => patch({ name, description: desc }, "Liste mise à jour")} data-testid="list-rename-submit">Enregistrer</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <ConfirmDialog open={confirm} onOpenChange={setConfirm} loading={busy} onConfirm={remove} title={`Supprimer « ${l.name} » ?`}
        description="La liste sera archivée dans Notion. Les contacts ne sont pas supprimés." />
    </div>
  );
}
